import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { Product } from '../Module/Product.model';
import { environment } from '../../environments/environment';

export interface PresignedUrlResponse {
  uploadUrl: string;
  fileUrl: string;
  objectKey: string;
  expiresAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private get apiUrl(): string {
    return `${environment.apiUrl}/products`;
  }

  constructor(private http: HttpClient) { }

  listProducts(): Observable<Product[]> {
    return this.http.get<Product[]>(this.apiUrl);
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  }

  addProduct(product: Product): Observable<Product> {
    return this.http.post<Product>(this.apiUrl, product);
  }

  updateProduct(product: Product): Observable<Product> {
    return this.http.put<Product>(this.apiUrl + '/' + product.codeBare, product);
  }

  getProduct(codeBare: string | number): Observable<Product> {
    return this.http.get<Product>(this.apiUrl + '/codeBarre/' + codeBare);
  }

  deleteProduct(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  updateProductQuantity(codeBare: string | number, quantity: number): Observable<Product> {
    return this.getProduct(codeBare).pipe(
      switchMap(product => {
        product.quantity = quantity;
        return this.http.put<Product>(this.apiUrl + '/codeBarre/' + codeBare, product);
      })
    );
  }

  updateProductByCodeBare(codeBare: string | number, product: Product): Observable<Product> {
    return this.http.put<Product>(this.apiUrl + '/codeBarre/' + codeBare, product);
  }

  presignUpload(fileName: string, contentType: string, codeBare?: string | number): Observable<PresignedUrlResponse> {
    return this.http.post<PresignedUrlResponse>(`${environment.apiUrl}/files/presign-upload`, {
      fileName,
      contentType,
      codeBare
    });
  }

  presignDownload(objectKey: string): Observable<PresignedUrlResponse> {
    return this.http.get<PresignedUrlResponse>(`${environment.apiUrl}/files/presign-download`, {
      params: { objectKey }
    });
  }

  // Category and Sub-category management
  getCategories(): Observable<string[]> {
    return this.http.get<string[]>(`${environment.apiUrl}/categories`);
  }

  getSubCategories(category: string): Observable<string[]> {
    return this.http.get<string[]>(`${environment.apiUrl}/categories/${category}/subcategories`);
  }

  getProductFamilies(category: string, subCategory: string): Observable<string[]> {
    return this.http.get<string[]>(`${environment.apiUrl}/categories/${category}/subcategories/${subCategory}/families`);
  }

  createCategory(category: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${environment.apiUrl}/categories`, { name: category });
  }

  createSubCategory(category: string, subCategory: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${environment.apiUrl}/categories/${category}/subcategories`, { name: subCategory });
  }

  createProductFamily(category: string, subCategory: string, productFamily: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(`${environment.apiUrl}/categories/${category}/subcategories/${subCategory}/families`, { name: productFamily });
  }
}


